import * as XLSX from 'xlsx';
import { EXPORT_TEMPLATES, EXPORT_NOTES, VALUE_FORMATTERS } from './ExcelTemplates';

// Generic export formatter using template system
const formatDataExport = (data, selectedMonths, viewType) => {
  const template = EXPORT_TEMPLATES[viewType];
  const workbook = XLSX.utils.book_new();
  
  // Create main data sheet
  const mainData = data.map(item => {
    const row = {};
    
    // Add basic columns
    template.columns.forEach(col => {
      const value = item[col.key] || (col.fallback ? item[col.fallback] : null);
      row[col.label] = VALUE_FORMATTERS[col.type](value);
    });

    // Add monthly columns
    selectedMonths.forEach(month => {
      template.monthlyColumns.forEach(monthCol => {
        if (monthCol.conditional) {
          // Handle conditional columns like MTD Sales
          const value = VALUE_FORMATTERS[monthCol.type](null, month, item);
          if (value !== null) {
            row[`${month} - ${monthCol.label}`] = value;
          }
        } else {
          const value = item[`${month}${monthCol.suffix}`];
          row[`${month} - ${monthCol.label}`] = VALUE_FORMATTERS[monthCol.type](value);
        }
      });
    });

    return row;
  });

  const worksheet = XLSX.utils.json_to_sheet(mainData);
  
  // Set column widths
  const baseColumnWidths = template.columns.map(col => ({ wch: col.width }));
  const monthlyColumnWidths = selectedMonths.flatMap(() => 
    template.monthlyColumns
      .filter(col => !col.conditional) // For now, exclude conditional columns from width calc
      .map(col => ({ wch: col.width }))
  );
  
  worksheet['!cols'] = [...baseColumnWidths, ...monthlyColumnWidths];
  
  XLSX.utils.book_append_sheet(workbook, worksheet, template.name);
  
  return workbook;
};

// Add summary sheet for detailed exports (SKU)
const addSummarySheet = (workbook, data, viewType) => {
  if (!EXPORT_TEMPLATES[viewType].includesSummary) return;
  
  const summaryData = createSummarySheet(data);
  const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
  summaryWorksheet['!cols'] = [
    { wch: 20 }, // Metric
    { wch: 15 }, // Value
  ];
  
  XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');
};

// Add notes sheet
const addNotesSheet = (workbook, viewType, filters) => {
  const notes = [
    { 'Notes': `${EXPORT_TEMPLATES[viewType].description}` },
    { 'Notes': '' },
    ...EXPORT_NOTES[viewType].map(note => ({ 'Notes': note })),
  ];

  if (filters.searchTerm || filters.statusFilter || filters.categoryFilter) {
    notes.push({ 'Notes': '' });
    notes.push({ 'Notes': 'Applied Filters:' });
    if (filters.searchTerm) notes.push({ 'Notes': `- Search: "${filters.searchTerm}"` });
    if (filters.statusFilter) notes.push({ 'Notes': `- Status: ${filters.statusFilter}` });
    if (filters.categoryFilter) notes.push({ 'Notes': `- Category: ${filters.categoryFilter}` });
  }

  const notesWorksheet = XLSX.utils.json_to_sheet(notes);
  notesWorksheet['!cols'] = [{ wch: 60 }];
  
  XLSX.utils.book_append_sheet(workbook, notesWorksheet, 'Notes');
};

// Create summary sheet for SKU export
const createSummarySheet = (data) => {
  const totalCurrentStock = data.reduce((sum, item) => sum + (item.currentStock || 0), 0);
  const totalItems = data.length;
  
  // Count by status
  const statusCounts = {};
  data.forEach(item => {
    const status = item.inventoryStatus?.label || 'Unknown';
    statusCounts[status] = (statusCounts[status] || 0) + 1;
  });

  const summary = [
    { 'Metric': 'Total Items', 'Value': totalItems },
    { 'Metric': 'Total Current Stock', 'Value': totalCurrentStock.toLocaleString() },
    { 'Metric': '', 'Value': '' }, // Empty row
    { 'Metric': 'Status Breakdown:', 'Value': '' },
    ...Object.entries(statusCounts).map(([status, count]) => ({
      'Metric': `  ${status}`,
      'Value': `${count} (${((count / totalItems) * 100).toFixed(1)}%)`
    }))
  ];

  return summary;
};

// Main export function
export const exportToExcel = (data, selectedMonths, viewType, filters = {}) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  if (!EXPORT_TEMPLATES[viewType]) {
    alert('Invalid view type');
    return;
  }

  try {
    // Create workbook with main data
    const workbook = formatDataExport(data, selectedMonths, viewType);
    
    // Add additional sheets
    addSummarySheet(workbook, data, viewType);
    addNotesSheet(workbook, viewType, filters);

    // Generate filename
    const template = EXPORT_TEMPLATES[viewType];
    const dateStr = new Date().toISOString().split('T')[0];
    const filterSuffix = (filters.searchTerm || filters.statusFilter || filters.categoryFilter) ? '_filtered' : '';
    const filename = `${template.name.replace(' ', '_')}_${dateStr}${filterSuffix}.xlsx`;

    // Export the file
    XLSX.writeFile(workbook, filename);
    
  } catch (error) {
    console.error('Export failed:', error);
    alert('Export failed. Please try again.');
  }
}; 
